// @ts-ignore
// @ts-nocheck
import React, { useEffect, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import PlusIcon from '../../icons/PlusIcon';
import { ArrowRightIcon, ArrowLeftIcon, MaximizeIcon, ExternalLinkIcon, SpotifyIcon, YoutubeIcon, SoundcloudIconSVG } from '../../icons';
import { isSpotifyTrack, isYouTubeVideo, isSoundCloudTrack } from '../../../utils/highlightUtil';
import { useCarousel } from '../../hooks/useCarousel';
import { getHighlightCarouselSlideKey } from '@/utils/highlightCarouselKeys';
import JamBoardPushpinDecor from '../../JamBoard/JamBoardPushpinDecor';
import 'swiper/css';
import 'swiper/css/pagination';
import './styles/minihighlights.css';

export interface MiniJWCarouselSlideProps {
    url?: string
    image?: {
        url: string
    }
    description?: string
    coverArtUrl?: string
    mediaUrl?: string
    typeCd?: string
    jamBoardPinColor?: string
    jamBoardRateLabel?: string
    jamBoardPinId?: string
}

export interface MiniJWCarouselProps {
    id: string
    addHighlightsClick?: () => void
    children?: React.ReactNode
    slides?: Array<MiniJWCarouselSlideProps> | null
    onSlideClick?: (item: MiniJWCarouselSlideProps, index: number) => void
    isLoading: boolean
    canEdit?: boolean
    slidesPerView?: number
    slidesPerGroup?: number
}

function MiniJWCarousel(props: MiniJWCarouselProps) {
    const {
        swiperComponent,
        showRightArrow,
        showLeftArrow,
        noSlides,
        isMobile,
        setSwiperComponent,
        slideNext,
        slidePrev,
        onSlideChange,
        onFromEdge,
        effectiveSlidesPerView,
        effectiveSlidesPerGroup
    } = useCarousel({
        slides: props.slides,
        initialSlidesPerView: props.slidesPerView || 3.5,
        initialSlidesPerGroup: props.slidesPerGroup || 2
    });

    const slidesIdentityKey = useMemo(
        () =>
            props.slides?.map((item, i) => getHighlightCarouselSlideKey(item, i)).join('|') ??
            '',
        [props.slides]
    );

    useEffect(() => {
        if (!swiperComponent || !props.slides?.length) return;
        swiperComponent.update();
    }, [swiperComponent, slidesIdentityKey, props.slides?.length]);

    const getLinkIcon = (item) => {
        if (item?.typeCd === 'JB') {
            return (
                <span className="external-link-icon inline-flex h-8 w-8 items-center justify-center overflow-visible">
                    <span className="scale-[0.7]">
                        <JamBoardPushpinDecor
                            accentColor={item?.jamBoardPinColor || '#dc2626'}
                        />
                    </span>
                </span>
            );
        }
        if (item?.url) {
            const url = item.url.toLowerCase();
            if (url.includes('spotify.com')) {
                return (
                    <svg width="16" height="16" viewBox="0 0 31 32" fill="white" className="external-link-icon">
                        <path d="M8.25456 22.3372C8.25456 21.9067 8.45638 21.5636 8.86003 21.3079C11.4568 19.7606 14.464 18.987 17.8815 18.987C19.671 18.987 21.6018 19.2157 23.6738 19.6732C24.2389 19.7943 24.5215 20.1441 24.5215 20.7227C24.5215 20.9918 24.4307 21.2238 24.249 21.4189C24.0674 21.614 23.8286 21.7116 23.5326 21.7116C23.4653 21.7116 23.2164 21.6578 22.7858 21.5501C21.0098 21.1868 19.375 21.0052 17.8815 21.0052C14.8407 21.0052 12.1699 21.6981 9.86914 23.084C9.6135 23.232 9.39149 23.306 9.20312 23.306C8.94748 23.306 8.72548 23.2152 8.53711 23.0335C8.34874 22.8519 8.25456 22.6198 8.25456 22.3372ZM6.31706 17.998C6.31706 17.4599 6.55252 17.0495 7.02344 16.7669C10.2122 14.8698 13.8989 13.9212 18.0833 13.9212C20.1419 13.9212 22.1803 14.2038 24.1986 14.7689C24.8444 14.9438 25.1673 15.3743 25.1673 16.0605C25.1673 16.3969 25.0496 16.6828 24.8141 16.9183C24.5787 17.1538 24.2928 17.2715 23.9564 17.2715C23.8622 17.2715 23.6133 17.2177 23.2096 17.11C21.5681 16.666 19.8796 16.444 18.1439 16.444C14.39 16.444 11.107 17.2782 8.29492 18.9466C7.97201 19.1215 7.71636 19.209 7.528 19.209C7.19162 19.209 6.90571 19.0913 6.67025 18.8558C6.43479 18.6203 6.31706 18.3344 6.31706 17.998ZM4.13737 12.9928C4.13737 12.3605 4.40647 11.8895 4.94466 11.5801C6.63997 10.5979 8.61111 9.85449 10.8581 9.34993C13.105 8.84538 15.4125 8.5931 17.7806 8.5931C20.5254 8.5931 22.9742 8.90929 25.127 9.54167C25.4364 9.63585 25.6954 9.8074 25.904 10.0563C26.1125 10.3052 26.2168 10.6315 26.2168 11.0352C26.2168 11.4523 26.0789 11.8021 25.8031 12.0846C25.5272 12.3672 25.1808 12.5085 24.7637 12.5085C24.6157 12.5085 24.3466 12.4546 23.9564 12.347C22.1669 11.8492 20.1016 11.6003 17.7604 11.6003C15.6211 11.6003 13.539 11.829 11.514 12.2865C9.48904 12.7439 7.78364 13.383 6.39779 14.2038C6.11523 14.3652 5.84614 14.446 5.5905 14.446C5.2003 14.446 4.86057 14.3081 4.57129 14.0322C4.28201 13.7564 4.13737 13.4099 4.13737 12.9928ZM0 16C0 18.8121 0.692926 21.4055 2.07878 23.7803C3.46463 26.1551 5.34494 28.0354 7.71973 29.4212C10.0945 30.8071 12.6879 31.5 15.5 31.5C18.3121 31.5 20.9055 30.8071 23.2803 29.4212C25.6551 28.0354 27.5354 26.1551 28.9212 23.7803C30.3071 21.4055 31 18.8121 31 16C31 13.1879 30.3071 10.5945 28.9212 8.21973C27.5354 5.84494 25.6551 3.96463 23.2803 2.57878C20.9055 1.19293 18.3121 0.5 15.5 0.5C12.6879 0.5 10.0945 1.19293 7.71973 2.57878C5.34494 3.96463 3.46463 5.84494 2.07878 8.21973C0.692926 10.5945 0 13.1879 0 16Z" />
                    </svg>
                );
            } else if (url.includes('youtube.com') || url.includes('youtu.be')) {
                return (
                    <svg width="21" height="21" viewBox="0 0 32 32" fill="white" className="w-5 h-5 flex-shrink-0 text-white ml-2">
                        <path d="M16.0519 0.5C7.49586 0.5 0.554199 7.45166 0.554199 16.0077C0.554199 24.5637 7.49586 31.5154 16.0519 31.5154C24.608 31.5154 31.5497 24.5637 31.5497 16.0077C31.5497 7.45166 24.608 0.5 16.0519 0.5ZM23.0932 16.6077C23.0932 16.6077 23.0932 19.7963 22.7932 21.345C22.6415 22.1161 22.0415 22.7161 21.2704 22.8678C19.7217 23.1678 16.0519 23.1678 16.0519 23.1678C16.0519 23.1678 12.382 23.1678 10.8333 22.8678C10.0623 22.7161 9.46226 22.1161 9.31059 21.345C9.01059 19.7963 9.01059 16.6077 9.01059 16.6077C9.01059 16.6077 9.01059 13.4191 9.31059 11.8704C9.46226 11.0993 10.0623 10.4993 10.8333 10.3477C12.382 10.0477 16.0519 10.0477 16.0519 10.0477C16.0519 10.0477 19.7217 10.0477 21.2704 10.3477C22.0415 10.4993 22.6415 11.0993 22.7932 11.8704C23.0932 13.4191 23.0932 16.6077 23.0932 16.6077Z" />
                        <path d="M14.1984 19.5022L18.8582 16.6081L14.1984 13.714V19.5022Z" />
                    </svg>
                );
            } else if (url.includes('soundcloud.com')) {
                return (
                    <svg width="26" height="14" viewBox="0 0 32 14" fill="white" className="external-link-icon">
                        <path d="M1.568 7.30334C1.5 7.30334 1.443 7.36034 1.432 7.43234L1.12 10.3073L1.432 13.1153C1.443 13.1883 1.5 13.2463 1.568 13.2463C1.636 13.2463 1.688 13.1893 1.697 13.1153L2.036 10.3073L1.677 7.43234C1.677 7.36034 1.62 7.30334 1.557 7.30334H1.568ZM0.36 8.40734C0.281 8.40734 0.24 8.45434 0.219 8.53134L0 10.3073L0.219 12.0473C0.219 12.1263 0.282 12.1783 0.339 12.1783C0.402 12.1783 0.459 12.1153 0.479 12.0373L0.76 10.2773L0.479 8.50134C0.479 8.41734 0.422 8.38134 0.36 8.38134V8.40734ZM2.797 6.74034C2.718 6.74034 2.64 6.80334 2.64 6.88034L2.36 10.2973L2.656 13.5733C2.656 13.6503 2.719 13.7343 2.817 13.7343C2.901 13.7343 2.958 13.6503 2.978 13.5733L3.317 10.2763L2.978 6.87534C2.958 6.79834 2.895 6.71934 2.817 6.71934L2.797 6.74034ZM4.079 6.59934C3.979 6.59934 3.896 6.67834 3.875 6.78234L3.62 10.2983L3.901 13.6943C3.921 13.7933 4 13.8753 4.098 13.8753C4.196 13.8753 4.281 13.7923 4.297 13.6783L4.62 10.2983L4.297 6.80334C4.2974 6.77971 4.29313 6.75622 4.28445 6.73423C4.27577 6.71224 4.26284 6.69217 4.24641 6.67518C4.22997 6.65819 4.21035 6.6446 4.18866 6.63519C4.16697 6.62578 4.14364 6.62073 4.12 6.62034L4.079 6.59934ZM5.62 7.07934C5.61668 7.02523 5.59275 6.97444 5.55313 6.93743C5.51351 6.90041 5.46122 6.87999 5.407 6.88034C5.287 6.88034 5.194 6.95934 5.188 7.07934L4.896 10.3183L5.161 13.7353C5.161 13.8553 5.265 13.9483 5.374 13.9483C5.473 13.9483 5.573 13.8553 5.573 13.7353L5.874 10.3183L5.573 7.05834L5.62 7.07934ZM6.697 4.80334C6.63409 4.80487 6.57418 4.83054 6.52969 4.87503C6.48519 4.91953 6.45952 4.97944 6.458 5.04234L6.177 10.3183L6.426 13.7353C6.426 13.8553 6.53 13.9543 6.666 13.9543C6.791 13.9543 6.895 13.8343 6.906 13.7143L7.182 10.2983L6.906 5.00134C6.895 4.86134 6.786 4.76134 6.666 4.76134L6.697 4.80334ZM7.943 3.54134C7.803 3.54134 7.683 3.66134 7.672 3.80234L7.432 10.2973L7.651 13.6933C7.651 13.8543 7.771 13.9733 7.912 13.9733C8.052 13.9733 8.172 13.8543 8.193 13.6933L8.453 10.2973L8.193 3.82234C8.172 3.66134 8.052 3.54134 7.917 3.54134H7.943ZM9.235 2.94334C9.074 2.94334 8.954 3.06334 8.932 3.21934L8.713 10.2553L8.932 13.6153C8.953 13.7713 9.073 13.9183 9.235 13.9183C9.31373 13.9153 9.3883 13.8822 9.44336 13.8258C9.49841 13.7694 9.52978 13.6941 9.531 13.6153L9.792 10.2553L9.531 3.21934C9.52851 3.14154 9.49656 3.06758 9.4416 3.01244C9.38665 2.9573 9.3128 2.92509 9.235 2.92234V2.94334ZM10.891 2.97934C10.8911 2.93768 10.883 2.8964 10.8671 2.85788C10.8513 2.81936 10.8279 2.78436 10.7984 2.7549C10.769 2.72543 10.734 2.70209 10.6955 2.68621C10.6569 2.67032 10.6157 2.66221 10.574 2.66234C10.413 2.66234 10.251 2.80334 10.251 2.97934L10.052 10.2353L10.251 13.5743C10.2577 13.6612 10.2966 13.7425 10.36 13.8022C10.4234 13.8619 10.5069 13.8959 10.594 13.8973C10.6363 13.8975 10.6781 13.8892 10.7172 13.873C10.7562 13.8567 10.7916 13.8329 10.8213 13.8028C10.851 13.7727 10.8744 13.737 10.8902 13.6978C10.9059 13.6586 10.9137 13.6166 10.913 13.5743L11.132 10.2353L10.913 2.96434L10.891 2.97934ZM11.891 2.80334C11.8021 2.8056 11.7175 2.84177 11.6545 2.90443C11.5914 2.9671 11.5548 3.0515 11.552 3.14034L11.349 10.2403L11.552 13.5363C11.552 13.7353 11.708 13.8753 11.891 13.8753C12.074 13.8753 12.23 13.7143 12.23 13.5163L12.433 10.2133L12.209 3.14034C12.2093 3.09313 12.2002 3.04632 12.1822 3.00265C12.1643 2.95897 12.1378 2.91929 12.1044 2.8859C12.0711 2.85251 12.0314 2.82608 11.9877 2.80813C11.944 2.79018 11.8972 2.78108 11.85 2.78134L11.891 2.80334ZM13.188 3.00034C13.0878 3.00114 12.992 3.04155 12.9216 3.11276C12.8511 3.18397 12.8117 3.28017 12.812 3.38034L12.672 10.2403L12.855 13.5363C12.8548 13.5859 12.8646 13.6349 12.8838 13.6806C12.903 13.7263 12.9311 13.7676 12.9665 13.8022C13.002 13.8368 13.044 13.864 13.0901 13.882C13.1362 13.9001 13.1855 13.9087 13.235 13.9073C13.432 13.9073 13.594 13.7463 13.61 13.5273L13.771 10.2713L13.594 3.45434C13.579 3.23534 13.413 3.07434 13.214 3.07434L13.188 3.00034ZM14.792 1.81234C14.7244 1.77423 14.6481 1.7542 14.5705 1.7542C14.4929 1.7542 14.4166 1.77423 14.349 1.81234C14.2899 1.84745 14.2407 1.89695 14.2059 1.95623C14.1711 2.0155 14.1518 2.08263 14.15 2.15134V2.23534L14.015 10.2983L14.166 13.5633V13.5743C14.1737 13.6623 14.2083 13.7457 14.265 13.8133C14.3047 13.8571 14.353 13.8922 14.4068 13.9164C14.4607 13.9407 14.5189 13.9536 14.578 13.9543C14.6814 13.9487 14.7794 13.9061 14.854 13.8343C14.9339 13.7538 14.9785 13.6448 14.978 13.5313L14.994 13.2143L15.15 10.2773L14.973 2.16234C14.9717 2.0986 14.9544 2.0362 14.9228 1.98083C14.8912 1.92546 14.8462 1.87888 14.792 1.84534V1.81234ZM16.131 1.08334C16.105 1.05737 16.0739 1.0371 16.0396 1.02384C16.0054 1.01058 15.9687 1.00462 15.932 1.00634C15.8328 1.00348 15.7349 1.03029 15.651 1.08334C15.6032 1.12289 15.5644 1.17233 15.5375 1.22825C15.5106 1.28418 15.4961 1.34528 15.495 1.40734V1.44334L15.312 10.2553L15.412 11.8753L15.495 13.4543C15.4999 13.5672 15.548 13.6739 15.6293 13.7522C15.7107 13.8306 15.819 13.8747 15.932 13.8753C16.172 13.8753 16.369 13.6723 16.369 13.4323L16.573 10.2133L16.369 1.36434C16.3656 1.28959 16.3438 1.21683 16.3056 1.15248C16.2674 1.08812 16.214 1.03416 16.15 0.995344L16.131 1.08334ZM28.063 6.03134C27.527 6.03134 27.006 6.14634 26.547 6.34434C26.3912 4.61798 25.595 3.0124 24.3152 1.8434C23.0353 0.674392 21.3644 0.0265688 19.631 0.0273444C18.75 0.0273444 17.886 0.208344 17.11 0.506344C16.807 0.626344 16.75 0.746344 16.73 0.985344V13.4743C16.75 13.7143 16.927 13.9183 17.173 13.9383H28.084C28.5979 13.9431 29.1077 13.8458 29.5837 13.6519C30.0597 13.4581 30.4925 13.1716 30.8569 12.8092C31.2213 12.4467 31.5101 12.0155 31.7065 11.5405C31.9029 11.0656 32.003 10.5563 32.001 10.0423C32.0023 9.52664 31.902 9.01573 31.7059 8.53878C31.5098 8.06183 31.2216 7.62819 30.8579 7.2626C30.4942 6.89702 30.062 6.60666 29.586 6.40809C29.1101 6.20953 28.5997 6.10666 28.084 6.10534L28.063 6.03134Z" />
                    </svg>
                );
            } else {
                return <ExternalLinkIcon className="w-4 h-4 external-link-icon" />;
            }
        }
        return null;
    };

    const renderMiniHighlightCard = (item, index) => {
        const handleCaptionClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (item?.url) {
                window.open(item.url, '_blank', 'noopener,noreferrer');
            } else {
                props?.onSlideClick?.(item, index);
            }
        };

        const handleSlideClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            // Only trigger click if it's not a drag/swipe event
            if (!e.nativeEvent.isTrusted || e.nativeEvent.type === 'click') {
                props?.onSlideClick?.(item, index);
            }
        };

        const hasCaption = Boolean(item?.description);

        return (
            <div
                className={`mini-highlight-card ${hasCaption ? 'has-caption' : ''}`}
                onClick={handleSlideClick}
            >
                <div className="image-container">
                    <img
                        src={item?.image?.url || item?.coverArtUrl || ''}
                        alt={item?.description || 'Highlight'}
                        loading={index > 0 ? 'lazy' : 'eager'}
                    />
                </div>
                <div className="maximize-button">
                    <MaximizeIcon />
                </div>
                {item?.typeCd === 'JB' && item?.jamBoardRateLabel && (
                    <div className="absolute top-2 left-2 z-10 rounded-full border border-white/40 bg-white/80 px-2.5 py-1 shadow-sm backdrop-blur-sm">
                        <span className="text-xs font-semibold tracking-wide text-[#1f2937]">
                            {item.jamBoardRateLabel}
                        </span>
                    </div>
                )}
                {item?.description && (
                    <div
                        className="title-container"
                        onClick={handleCaptionClick}
                    >
                        <div className="title-content">
                            <p>{item?.description || ''}</p>
                            {getLinkIcon(item)}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="mini-carousel-container">
            {/* Left Arrow Button - always render but conditionally show */}
            <button
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    slidePrev();
                }}
                className={`left-arrow ${!showLeftArrow ? 'opacity-0 pointer-events-none' : ''}`}
            >
                <div>
                    <ArrowLeftIcon id={props.id} />
                </div>
            </button>

            {noSlides && props.canEdit ? (
                <div className="no-slides text-purple" />
            ) : (
                <Swiper
                    spaceBetween={8}
                    slidesPerView={effectiveSlidesPerView}
                    slidesPerGroup={effectiveSlidesPerGroup}
                    onSlideChange={onSlideChange}
                    onFromEdge={onFromEdge}
                    onSwiper={setSwiperComponent}
                    modules={[Pagination]}
                    centeredSlides={isMobile || props.slidesPerView === 1}
                    preventInteractionOnTransition={true}
                    touchRatio={1}
                    touchAngle={45}
                    touchMoveStopPropagation={true}
                >
                    {/* SwiperSlides have to be direct children of the Swiper component */}
                    {props?.slides?.map((item, index) => (
                        <SwiperSlide key={getHighlightCarouselSlideKey(item, index)}>
                            {renderMiniHighlightCard(item, index)}
                        </SwiperSlide>
                    ))}
                </Swiper>
            )}

            {/* Right Arrow Button - always render but conditionally show */}
            <button
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    slideNext();
                }}
                className={`right-arrow ${!showRightArrow ? 'opacity-0 pointer-events-none' : ''}`}
            >
                <div>
                    <ArrowRightIcon id={props.id} />
                </div>
            </button>
        </div>
    );
}

export default MiniJWCarousel;
